import Mock from "../mock";
import { getAge, getExperienceTime } from "../../lib/utils";

export interface Skill {
  [key: string]: number;
}

export interface WorkExperience {
  id: number;
  year: string;
  position: string;
  company: string;
  start: number;
  end: number | string;
  skills: Skill;
  details: string;
}

export interface EducationExperience {
  id: number;
  year: string;
  graduation: string;
  university: string;
  details: string;
}

export interface Interest {
  title: string;
  icon: string;
  details: string;
}

export interface Review {
  id: number;
  content: string;
  author: {
    name: string;
    designation: string;
  };
}

export interface SkillItem {
  title: string;
  value: number;
}

export interface PortfolioItem {
  id: number;
  title: string;
  subtitle: string;
  imageUrl: string;
  largeImageUrl?: string[];
  url: string;
}

export interface BlogPost {
  id: number;
  title: string;
  description: string;
  featuredImage: string;
  filesource: string;
  createDay: string;
  createMonth: string;
  createYear: string;
  publishedAt: string;
  updatedAt: string;
}

export interface ShowcaseItem {
  id: string;
  title: string;
  description: string;
  tags: string[];
  featuredImage: string;
  createDay: string;
  createMonth: string;
  createYear: string;
  publishedAt: string;
  updatedAt: string;
}

export interface ContactInfo {
  phoneNumbers: string[];
  emailAddress: string[];
  address: string;
}

export interface ServiceOffering {
  id: number;
  title: string;
  icon: string;
  shortDescription: string;
  details: string;
  deliverables: string[];
  timeline?: string;
  idealFor: string[];
}

export interface ServicesPageData {
  headline: string;
  introduction: string;
  valueProposition: string;
  services: ServiceOffering[];
  callToAction: string;
}

export interface Information {
  name: string;
  aboutContent: string;
  personalContent: string;
  age: number;
  phone: string;
  nationality: string;
  language: string;
  email: string;
  address: string;
  freelanceStatus: string;
  socialLinks: {
    facebook: string;
    pinterest: string;
    behance: string;
    linkedin: string;
    dribbble: string;
    github: string;
  };
  brandImage: string;
  aboutImage: string;
  aboutImageLg: string;
  cvfile: string;
  cvLink: string;
}

export interface Database {
  information: Information;
  interests: Interest[];
  reviews: Review[];
  skills: SkillItem[];
  portfolio: PortfolioItem[];
  experience: {
    workExperience: WorkExperience[];
    educationExperience: EducationExperience[];
  };
  blog: BlogPost[];
  showcase: ShowcaseItem[];
  contactInfo: ContactInfo;
  servicesPage: ServicesPageData;
}

const workExperience: WorkExperience[] = [
  {
    id: 6,
    year: "2013 - 2015",
    position: "Frontend Developer",
    company: "Auburn Creative",
    start: 2013,
    end: 2015,
    skills: {
      AngularJS: 0,
      HTML: 50,
      CSS: 60,
      JavaScript: 50,
      TypeScript: 0,
      React: 0,
      GraphQL: 0,
      NodeJS: 0,
      DesignSystems: 0,
      Vue: 0,
      Wordpress: 80,
    },
    details:
      "Creating WordPress themes, plugins & widgets. Providing personalised client training to organisations as well as large international companies. Evaluating client requirements & translating into long-term solutions to work across all platforms & browsers. Creating development standards & kits for external contractors. Interviewing contractors & analysing their coding abilities. Determining accurate project costing & build timescales.",
  },
  {
    id: 5,
    year: "2015 - 2017",
    position: "Web Developer",
    company: "Metia",
    start: 2015,
    end: 2017,
    skills: {
      AngularJS: 70,
      HTML: 70,
      CSS: 70,
      JavaScript: 70,
      TypeScript: 20,
      React: 60,
      GraphQL: 40,
      NodeJS: 50,
      DesignSystems: 0,
      Vue: 0,
      Wordpress: 70,
    },
    details:
      "Developing .NET and Angular JS websites for high-end clients such as Dell & Microsoft. Working closely with designers, informing them on new technologies and UX trends.",
  },
  {
    id: 4,
    year: "2017 - 2019",
    position: "Frontend Engineer",
    company: "Profile Pensions",
    start: 2017,
    end: 2019,
    skills: {
      AngularJS: 60,
      HTML: 70,
      CSS: 70,
      JavaScript: 70,
      TypeScript: 20,
      React: 60,
      GraphQL: 40,
      NodeJS: 50,
      DesignSystems: 0,
      Vue: 0,
      Wordpress: 60,
    },
    details:
      "Developing and maintaining internal web apps using React, Redux and Node working closely with backend developers. Building progressive web apps to improve the user experience for our customers.",
  },
  {
    id: 3,
    year: "2019 - 2020",
    position: "Software Engineer",
    company: "Bravissimo",
    start: 2019,
    end: 2020,
    skills: {
      AngularJS: 30,
      HTML: 90,
      CSS: 90,
      JavaScript: 90,
      TypeScript: 80,
      React: 90,
      GraphQL: 70,
      NodeJS: 50,
      DesignSystems: 80,
      Vue: 0,
      Wordpress: 50,
    },
    details:
      "Maintaining a full-stack website (React, Redux, Node, SQL) application and internal tools, being on hand to provide help to junior developers. Providing learnings and reports from previous sprints and bringing new technologies, including researching and building MVPs.",
  },
  {
    id: 2,
    year: "2020 - 2023",
    position: "Senior JavaScript Engineer",
    company: "ManyPets",
    start: 2020,
    end: 2023,
    skills: {
      AngularJS: 30,
      HTML: 90,
      CSS: 90,
      JavaScript: 90,
      TypeScript: 80,
      React: 90,
      GraphQL: 70,
      NodeJS: 50,
      DesignSystems: 80,
      Vue: 60,
      Wordpress: 40,
    },
    details:
      "Working in a data-driven environment across multiple squads maintaining and developing progressive web apps and a patterns library. Involved in multiple councils with responsibilities such as accessibility, front-end foundations and SEO. Running sprint ceremonies and reporting on DORA metrics.",
  },
  {
    id: 1,
    year: "2023 - 2024",
    position: "Lead Frontend Engineer/Full Stack Developer",
    company: "Wise",
    start: 2023,
    end: 2024,
    skills: {
      AngularJS: 20,
      HTML: 90,
      CSS: 90,
      JavaScript: 90,
      TypeScript: 80,
      React: 90,
      GraphQL: 70,
      NodeJS: 50,
      DesignSystems: 80,
      Vue: 40,
      Wordpress: 40,
    },
    details:
      "Working with Remix.js, React, Node, TypeScript, and GraphQL to build a new platform for Wise. Mentoring frontend engineers and working closely with the design team to create a new design system.",
  },
  {
    id: 0,
    year: "2024 - Present",
    position: "Lead Frontend Engineer",
    company: "Scaleable",
    start: 2024,
    end: new Date().getFullYear().toString(),
    skills: {
      AngularJS: 5,
      HTML: 95,
      CSS: 95,
      JavaScript: 95,
      TypeScript: 95,
      React: 95,
      GraphQL: 50,
      NodeJS: 70,
      DesignSystems: 90,
      Vue: 20,
      Wordpress: 15,
    },
    details:
      "Building a workforce analytics platform that processes device activity data to deliver actionable intelligence.  Translate complex workforce data into intuitive, actionable visualisation.",
  },
];

const database: Database = {
  information: {
    name: "Nathan Hensher",
    aboutContent: `I'm a JavaScript engineer specialising in frontend development. With over ${getExperienceTime()} years experiance and extensive skills in Vue, React, TypeScript, and Node, I have developed a passion for creating innovative and user-friendly applications that make a difference in people's lives.`,
    personalContent:
      "Technology has always been a significant part of my life, both in and out of the workplace. I have a particular interest in computer hardware, which leads me to enjoy tinkering with electronics in my spare time. This hands-on approach helps me stay informed about new developments in the field and continually expand my knowledge and skills. While I find great satisfaction in my professional pursuits, my family is the cornerstone of my life. As a father to three children and to our whippet, Ralph, I've found that my personal life greatly influences my professional goals. My family provides the motivation that drives me to grow and improve in all aspects of life. This combination of technical curiosity and strong family ties shapes how I approach both work and personal challenges. It encourages me to think creatively, work diligently, and always keep in mind the bigger picture - not just for my career, but for the future I'm building with my family.",
    age: getAge("1991-03-10"),
    phone: "+447833592682",
    nationality: "British",
    language: "",
    email: "me@nathanhensher.dev",
    address: "Leamington Spa, UK",
    freelanceStatus: "Available",
    socialLinks: {
      facebook: "",
      // twitter: "https://twitter.com/nhensh",
      pinterest: "",
      behance: "",
      linkedin: "https://www.linkedin.com/in/nathan-hensher-43267956/",
      dribbble: "",
      github: "https://github.com/nhensh",
    },
    brandImage: "/images/brand-image.jpg",
    aboutImage: "/images/about-image.jpg",
    aboutImageLg: "/images/about-image-lg.jpg",
    cvfile: "/files/Nathan-Hensher-CV.pdf",
    cvLink:
      "https://docs.google.com/document/d/1I83lt_GOwx28ygiOCHQlnD2hekA-B2m-QrganFBrM58/edit?usp=sharing",
  },
  interests: [
    {
      title: "Electronics",
      icon: "react-lineicons",
      details:
        "Passionate about Raspberry Pis and microcontrollers, exploring their capabilities and applications.",
    },
    {
      title: "Football",
      icon: "contest",
      details:
        "Avid supporter of Liverpool FC and enjoy playing 5-a-side football regularly.",
    },
    {
      title: "Technology",
      icon: "lni-ux",
      details:
        "Keen interest in the latest TVs and PC components, staying up-to-date with cutting-edge tech.",
    },
    {
      title: "Gaming",
      icon: "lni-game",
      details:
        "Enthusiast gamer, enjoying various video game platforms and titles.",
    },
    {
      title: "Social",
      icon: "beer",
      details:
        "Enjoy unwinding with a beer at the pub, combining socializing with relaxation.",
    },
  ],
  reviews: [
    {
      id: 1,
      content:
        "Nathan has proven himself to be highly capable of operating at a senior level with minimal management. He consistently delivers complex functionality and well-considered architecture while working effectively with senior stakeholders, product management, fellow developers, and operations teams. He is highly skilled in using AI as part of his workflow, with particular strengths in analysing complex data and delivering well-designed interfaces.",
      author: {
        name: "Nick Youell",
        designation: "EVP Products at Scalable Software",
      },
    },
    {
      id: 2,
      content:
        "Nathan is a very hard working and capable developer, excellent under pressure and a great communicator. I worked with Nathan in my capacity as a tester during a very hectic period and he always had a fantastic attitude and produced great code. A pleasure to work with!",
      author: {
        name: "Taran Acutt",
        designation: "Senior Test Analyst at Scalable Software",
      },
    },
    {
      id: 3,
      content:
        "I'm pleased to recommend my colleague, Nathan, for his outstanding work as a Lead Frontend Engineer. Having worked alongside him for my entire tenure at ManyPets, I can confidently vouch for his skills and qualities. Nathan is detail-oriented, ensuring our projects are always polished and up to high standards. He's hardworking and motivated, which keeps our team on track and inspired to push forward. His problem-solving abilities are impressive, allowing him to tackle complex challenges and stay updated on industry trends. As a personable team player, Nathan contributes positively to our work environment, making collaboration enjoyable and efficient. In summary, Nathan is a talented Lead Frontend Engineer who consistently delivers quality work and fosters a productive team atmosphere.",
      author: {
        name: "Sam Roberts",
        designation: "Frontend Engineer at ManyPets",
      },
    },
    {
      id: 4,
      content:
        "I worked with Nathan for over a year, he was the first point of contact when I joined as a contractor and got me set up and onboarded quickly. He continued to be a great team member when I joined full time. He's got great knowledge of web fundamentals and produces simple solutions to complex problems. He'd be a great asset to any team he joins.",
      author: {
        name: "Ollie Baker",
        designation: "Senior Frontend Developer",
      },
    },
    {
      id: 4,
      content:
        "I have had the pleasure of working with Nathan for the last 3 years; he has been be a strong contributor within the squads we have collaborated together in and played a vital role in delivering critical initiatives. I have always found him to be a very dependable member of the team and someone that has a great deal of technical ability that I have countlessly relied on get projects over the line. I would take great comfort in Nathan being involved in any project I'm working on in the future and hope to get the opportunity to work with him again.",
      author: {
        name: "Steven Roberts",
        designation: "Technical Business Analyst at ManyPets",
      },
    },
  ],
  skills: [
    {
      title: "HTML",
      value: 95,
    },
    {
      title: "CSS",
      value: 90,
    },
    {
      title: "React",
      value: 90,
    },
    {
      title: "Javascript",
      value: 85,
    },
    {
      title: "Design Systems",
      value: 85,
    },
    {
      title: "Vue",
      value: 85,
    },
    {
      title: "TypeScript",
      value: 75,
    },
    {
      title: "NodeJS",
      value: 70,
    },
    {
      title: "SEO",
      value: 70,
    },
    {
      title: "WordPress",
      value: 65,
    },
    {
      title: "GraphQL",
      value: 60,
    },
    {
      title: "D3.js",
      value: 30,
    },
    {
      title: "Go",
      value: 20,
    },
  ],
  portfolio: [
    {
      id: 1,
      title: "TCG-Placeholder.com",
      subtitle: "The place for master set collections and placeholder cards.",
      imageUrl: "/images/TCG-Placeholder-com.png",
      url: "https://tcg-placeholder.com/",
    },
    {
      id: 2,
      title: "ManyPets",
      subtitle: "A Nuxt.JS website for a pet insurance company.",
      imageUrl: "/images/ManyPets.jpeg",
      largeImageUrl: ["/images/ManyPets.jpeg"],
      url: "https://manypets.com",
    },
    {
      id: 3,
      title: "ManyGroup",
      subtitle: "A Nuxt.JS website for an insurance group.",
      imageUrl: "/images/ManyGroup.jpeg",
      url: "https://many-group.com",
    },
    {
      id: 4,
      title: "Factourism",
      subtitle: "A Wordpress website for facts, ",
      imageUrl: "/images/Factourism.gif",
      largeImageUrl: ["/images/Factourism.jpg"],
      url: "https://factourism.com",
    },
    {
      id: 5,
      title: "Bravissimo",
      subtitle:
        "A Next.JS website for a lingerie company with a custom ecommerce system.",
      imageUrl: "/images/Bravissimo.jpeg",
      url: "https://bravissimo.com",
    },
    {
      id: 6,
      title: "Profile Pensions",
      subtitle: "A Next.JS website for a pension advice fintech company",
      imageUrl: "/images/ProfilePensions.jpeg",
      largeImageUrl: ["/images/ProfilePensions.jpeg"],
      url: "https://profilepensions.co.uk",
    },
    {
      id: 7,
      title: "Data Viz Project",
      subtitle: "A data visualisation project using D3.js.",
      imageUrl: "/images/DataVizProject.gif",
      largeImageUrl: ["/images/DataVizProject.png"],
      url: "https://datavizproject.com",
    },
    {
      id: 8,
      title: "Metia",
      subtitle: "A .NET website for a marketing agency.",
      imageUrl: "/images/Metia.jpg",
      url: "https://metia.com/contact/?location=London#locations",
    },
  ],
  experience: {
    workExperience,
    educationExperience: [
      {
        id: 1,
        year: "2010 - 2013",
        graduation: "Bachelor of Science - 2:1",
        university: "University of Worcester (BSc Hons)",
        details: "Computer Science with Business Management",
      },
      {
        id: 2,
        year: "2010 - 2013",
        graduation: "Foundatin Degree of Science - Distinction",
        university: "Bournemouth University",
        details: "Computing",
      },
      {
        id: 3,
        year: "2007 - 2009",
        graduation: "Information Technologies - A Level",
        university: "Stratford Upon Avon 6th From",
        details: "",
      },
      {
        id: 4,
        year: "2007 - 2009",
        graduation: "Communication Studies - A Level",
        university: "Stratford Upon Avon College",
        details: "",
      },
      {
        id: 5,
        year: "2007 - 2009",
        graduation: "Fine Art - A Level",
        university: "Stratford Upon Avon 6th From",
        details: "",
      },
    ],
  },
  blog: [
    {
      id: 0,
      title: "The Rise of Asynchronous Work: Without Becoming a Hermit",
      description:
        "The rise of remote work has been a hot topic in recent years, with many companies adopting asynchronous work practices. But what does this mean for you? In this blog post, we explore the benefits and challenges of asynchronous work and how you can make it work for you.",
      featuredImage: "/images/blog-image-4.jpg",
      filesource: "../../blog/rise-of-async-work.md",
      createDay: "20",
      createMonth: "February",
      createYear: "2021",
      publishedAt: "2021-02-20",
      updatedAt: "2021-02-20",
    },
    {
      id: 1,
      title:
        "The Final Nail in Lodash's Coffin: JavaScript's New Array Grouping",
      description:
        "Lodash has been a staple in JavaScript development for years, but with the introduction of new array grouping methods in ES6, is it time to say goodbye? In this blog post, we explore the benefits of using native JavaScript methods over Lodash and how you can make the switch.",
      featuredImage: "/images/blog-image-3.jpg",
      filesource: "../../blog/final-nail-in-lodash-coffin.md",
      createDay: "1",
      createMonth: "July",
      createYear: "2024",
      publishedAt: "2024-07-01",
      updatedAt: "2024-10-01",
    },
    {
      id: 2,
      title:
        "Build vs Buy: Why Custom Component Libraries Beat Pre-Built Solutions",
      description:
        "Should your company invest in building a custom component library or use pre-built solutions like Kendo UI? In this post, I explore the long-term benefits of owning your design system and how it enables teams to scale without friction.",
      featuredImage: "/images/blog-image-1.jpg",
      filesource: "../../blog/build-vs-buy-component-libraries.md",
      createDay: "19",
      createMonth: "December",
      createYear: "2025",
      publishedAt: "2025-12-19",
      updatedAt: "2025-12-19",
    },
    {
      id: 3,
      title: "Frontend Architecture for Scale: Building for AWS",
      description:
        "A well-designed frontend architecture is crucial for scaling applications on AWS. Learn the patterns, strategies, and best practices that enable your frontend to handle growth while maintaining performance and developer productivity.",
      featuredImage: "/images/blog-image-6.jpg",
      filesource: "../../blog/frontend-architecture-aws-scale.md",
      createDay: "19",
      createMonth: "December",
      createYear: "2025",
      publishedAt: "2025-12-19",
      updatedAt: "2025-12-19",
    },
    {
      id: 4,
      title: "The Future of Frontend: Beyond the Browser",
      description:
        "The user interface is leaving the browser. From AR and voice to headless APIs and local-first computing, this post explores where frontend engineering is heading and how to prepare for it.",
      featuredImage: "/images/blog-image-2.jpg",
      filesource: "../../blog/future-of-frontend-beyond-browser.md",
      createDay: "22",
      createMonth: "January",
      createYear: "2026",
      publishedAt: "2026-01-22",
      updatedAt: "2026-01-22",
    },
    {
      id: 5,
      title: "Edge Rendering and the Rise of Local-First Applications",
      description:
        "Local-first architectures and edge rendering are reshaping how frontend applications store, sync, and compute data. Learn how CRDTs, local state, and edge functions work together.",
      featuredImage: "/images/blog-image-5.jpg",
      filesource: "../../blog/edge-rendering-local-first-applications.md",
      createDay: "18",
      createMonth: "February",
      createYear: "2026",
      publishedAt: "2026-02-18",
      updatedAt: "2026-02-18",
    },
    {
      id: 6,
      title:
        "Design Tokens, AI Assistants and the Collapse of the Design Handoff",
      description:
        "Design tokens and AI-generated code are eliminating the traditional handoff between designers and developers. This post explains how a shared design system becomes the single source of truth.",
      featuredImage: "/images/blog-image-7.jpg",
      filesource: "../../blog/design-tokens-ai-assistants-handoff.md",
      createDay: "21",
      createMonth: "March",
      createYear: "2026",
      publishedAt: "2026-03-21",
      updatedAt: "2026-03-21",
    },
    {
      id: 7,
      title: "Why the Monolithic Frontend is Making a Comeback",
      description:
        "Micro-frontends promised autonomy but brought complexity. The modular monolith is returning as a simpler, high-performing alternative for many product teams.",
      featuredImage: "/images/blog-image-8.jpg",
      filesource: "../../blog/monolithic-frontend-comeback.md",
      createDay: "25",
      createMonth: "April",
      createYear: "2026",
      publishedAt: "2026-04-25",
      updatedAt: "2026-04-25",
    },
    {
      id: 8,
      title: "Building Agentic AI Systems for the Software Delivery Lifecycle",
      description:
        "Agentic AI moves beyond assistants to autonomous systems that plan, execute, and self-correct. This post covers multi-agent SDLC architectures, state machines, and guardrails.",
      featuredImage: "/images/blog-image-9.jpg",
      filesource: "../../blog/building-agentic-ai-systems-sdlc.md",
      createDay: "19",
      createMonth: "May",
      createYear: "2026",
      publishedAt: "2026-05-19",
      updatedAt: "2026-05-19",
    },
    {
      id: 9,
      title:
        "Automated UI Delivery: From Figma to Production with Self-Healing Agents",
      description:
        "A complete walkthrough of an automated UI delivery pipeline where agents extract design tokens, generate code, run tests, self-heal failures, and deploy to staging.",
      featuredImage: "/images/blog-image-1.jpg",
      filesource: "../../blog/automated-ui-delivery-figma-production.md",
      createDay: "15",
      createMonth: "July",
      createYear: "2026",
      publishedAt: "2026-07-15",
      updatedAt: "2026-07-15",
    },
  ],
  showcase: [
    {
      id: "ColourPaletteGenerator",
      title: "Colour Palette Generator",
      description: "A simple colour palette generator.",
      tags: ["React", "JavaScript", "CSS"],
      featuredImage: "/images/ColourPaletteGenerator.gif",
      createDay: "1",
      createMonth: "March",
      createYear: "2021",
      publishedAt: "2021-03-01",
      updatedAt: "2021-03-01",
    },
    {
      id: "ThemeToggle",
      title: "Light/Dark Toggle",
      description: "A simple light/dark mode toggle switch.",
      tags: ["React", "JavaScript", "CSS"],
      featuredImage: "/images/ThemeToggle.gif",
      createDay: "18",
      createMonth: "October",
      createYear: "2022",
      publishedAt: "2022-10-18",
      updatedAt: "2022-10-18",
    },
    {
      id: "ShowcaseWorkHistoryTimeline",
      title: "Work History Timeline",
      description: "A timeline of my work history.",
      tags: ["React", "JavaScript", "CSS", "D3.js"],
      featuredImage: "/images/WorkHistoryTimeline.jpg",
      createDay: "10",
      createMonth: "August",
      createYear: "2023",
      publishedAt: "2023-08-10",
      updatedAt: "2024-10-30",
    },
    {
      id: "ShowcaseSkeletonLoader",
      title: "Skeleton Loader",
      description: "A simple skeleton loader component.",
      tags: ["React", "JavaScript", "CSS"],
      featuredImage: "/images/SkeletonLoader.gif",
      createDay: "15",
      createMonth: "August",
      createYear: "2023",
      publishedAt: "2023-08-15",
      updatedAt: "2023-08-15",
    },
    {
      id: "BandedPaymentSlider",
      title: "Banded Range Slider",
      description: "A range slider with banded values.",
      tags: ["React", "JavaScript", "CSS"],
      featuredImage: "/images/BandedPaymentSlider.gif",
      createDay: "12",
      createMonth: "September",
      createYear: "2024",
      publishedAt: "2024-09-12",
      updatedAt: "2024-09-12",
    },
    {
      id: "ShowcaseDotPlot",
      title: "DotPlot Visualisation",
      description:
        "An interactive dot plot visualisation component with multiple layout types.",
      tags: ["React", "TypeScript", "Konva", "D3.js", "styled-components"],
      featuredImage: "/images/DotPlot.gif",
      createDay: "18",
      createMonth: "December",
      createYear: "2024",
      publishedAt: "2024-12-18",
      updatedAt: "2024-12-18",
    },
    {
      id: "AgenticPipelineVisualizer",
      title: "Agentic Delivery Pipeline Visualizer",
      description:
        "An interactive, animated demonstration of an autonomous SDLC delivery pipeline with design, product, engineering, QA and self-healing stages.",
      tags: ["React", "TypeScript", "Framer Motion", "Tailwind CSS"],
      featuredImage: "/images/AgenticPipelineVisualizer.svg",
      createDay: "15",
      createMonth: "July",
      createYear: "2026",
      publishedAt: "2026-07-15",
      updatedAt: "2026-07-15",
    },
    // {
    //   id: "KochSnowflake",
    //   title: "Koch Snowflake",
    //   description: "A simple Koch snowflake animation using D3.js.",
    //   tags: ["React", "JavaScript", "CSS", "D3.js"],
    //   featuredImage: "/images/KochSnowflake.gif",
    //   createDay: "04",
    //   createMonth: "November",
    //   createYear: "2024",
    //   publishedAt: "2024-11-04",
    // },
    // {
    //   id: "LondonSkyline",
    //   title: "London Skyline",
    //   description: "A simple London skyline animation.",
    //   tags: ["React", "JavaScript", "CSS"],
    //   featuredImage: "/images/LondonSkyline.gif",
    //   createDay: "12",
    //   createMonth: "September",
    //   createYear: "2023",
    //   publishedAt: "2023-09-12",
    //   updatedAt: "2023-09-12",
    // },
  ],
  contactInfo: {
    phoneNumbers: ["+447833592682"],
    emailAddress: ["me@nathanhensher.dev"],
    address: "Leamington Spa, UK",
  },
  servicesPage: {
    headline: "Accelerate Your Product Development",
    introduction: `With over ${getExperienceTime()} years of experience building scalable frontend architectures for companies like Wise, ManyPets, and Bravissimo, I help startups and growing businesses ship faster without accumulating technical debt.`,
    valueProposition:
      "I specialise in building robust component libraries and frontend architecture that enables your team to scale efficiently. My approach focuses on delivering production-ready foundations in 4 weeks that will serve your business for years to come.",
    services: [
      {
        id: 1,
        title: "Speed-to-Market MVP Development",
        icon: "rocket",
        shortDescription:
          "Launch your product faster with a solid technical foundation.",
        details:
          "Get your MVP to market in weeks, not months. I build lean, functional products using modern frameworks like React, Next.js, and Remix that are designed to scale. Unlike typical MVPs that become technical debt, mine are built with production-quality architecture from day one.",
        deliverables: [
          "Fully functional MVP application",
          "Scalable component library",
          "CI/CD pipeline setup",
          "Documentation and handover",
          "Design system foundations",
        ],
        timeline: "4-6 weeks",
        idealFor: [
          "Startups seeking seed funding",
          "Businesses validating new product ideas",
          "Teams needing rapid prototyping",
        ],
      },
      {
        id: 2,
        title: "Frontend Architecture Consultancy",
        icon: "layers",
        shortDescription:
          "Build a frontend foundation that scales with your business.",
        details:
          "I design and implement frontend architectures that enable teams to move fast without breaking things. Drawing from my experience leading frontend teams at scale-ups, I establish patterns, tooling, and practices that reduce development friction and improve code quality.",
        deliverables: [
          "Architecture design document",
          "Component library setup (Storybook)",
          "Testing strategy and implementation",
          "Performance optimisation audit",
          "Team training and knowledge transfer",
        ],
        timeline: "2-4 weeks",
        idealFor: [
          "Growing engineering teams",
          "Companies modernising legacy frontends",
          "Startups preparing for scale",
        ],
      },
      {
        id: 3,
        title: "Design System Development",
        icon: "grid-alt",
        shortDescription:
          "Consistent, reusable components that accelerate development.",
        details:
          "I create comprehensive design systems that bridge the gap between design and development. With experience building pattern libraries at ManyPets and Wise, I deliver component libraries that ensure visual consistency, improve accessibility, and dramatically speed up feature development.",
        deliverables: [
          "Fully documented component library",
          "Storybook integration",
          "Accessibility compliance (WCAG 2.1)",
          "Theming and customisation system",
          "Integration guidelines",
        ],
        timeline: "4-8 weeks",
        idealFor: [
          "Companies with multiple products",
          "Teams struggling with UI consistency",
          "Organisations prioritising accessibility",
        ],
      },
      {
        id: 4,
        title: "Technical Due Diligence",
        icon: "search-alt",
        shortDescription:
          "Expert assessment of frontend codebases and architecture.",
        details:
          "Considering an acquisition or investment? I provide thorough technical assessments of frontend codebases, identifying risks, technical debt, and opportunities. My reports give you the clarity needed to make informed decisions.",
        deliverables: [
          "Comprehensive codebase audit",
          "Technical debt assessment",
          "Scalability analysis",
          "Risk identification report",
          "Remediation roadmap",
        ],
        timeline: "1-2 weeks",
        idealFor: [
          "Investors evaluating tech companies",
          "Companies considering acquisitions",
          "CTOs inheriting legacy systems",
        ],
      },
    ],
    callToAction:
      "Ready to accelerate your product development? Let's discuss how I can help your team ship faster and scale smarter.",
  },
};

Mock.onGet("/api/information").reply((config) => {
  const response = database.information;
  return [200, response];
});

Mock.onGet("/api/interests").reply((config) => {
  const response = database.interests;
  return [200, response];
});

Mock.onGet("/api/reviews").reply((config) => {
  const response = database.reviews;
  return [200, response];
});

Mock.onGet("/api/skills").reply((config) => {
  const response = database.skills;
  return [200, response];
});

Mock.onGet("/api/portfolio").reply((config) => {
  const response = database.portfolio;
  return [200, response];
});

Mock.onGet("/api/experience").reply((config) => {
  const response = database.experience;
  return [200, response];
});

Mock.onGet("/api/blog").reply((config) => {
  const response = database.blog;
  return [200, response];
});

Mock.onGet(/\/api\/blog\/(\d+)/).reply((config) => {
  const id = parseInt(config.url?.split("/").pop() || "0");
  const post = database.blog.find((item) => item.id === id);
  return post ? [200, post] : [404, { message: "Blog post not found" }];
});

Mock.onGet("/api/showcase").reply((config) => {
  const response = database.showcase;
  return [200, response];
});

Mock.onGet(/\/api\/showcase\/(.+)/).reply((config) => {
  const id = config.url?.split("/").pop() || "";
  const showcase = database.showcase.find((item) => item.id === id);
  return showcase ? [200, showcase] : [404, { message: "Showcase not found" }];
});

Mock.onGet("/api/contactinfo").reply((config) => {
  const response = database.contactInfo;
  return [200, response];
});

Mock.onGet("/api/services").reply((config) => {
  const response = database.servicesPage;
  return [200, response];
});

export default database;
